import {
  LotteryBatchStateGroup,
  LotteryBatchStepStatus,
} from 'src/types/Lottery';

export const DISPOSITION_LABELS: Record<string, string> = {
  in_batch: 'In Batch',
  request_for_documents: 'Request For Documents',
  rejected: 'Rejected',
  ineligible: 'Ineligible',
  set_as_pending: 'Set as Pending',
  not_interested: 'Not interested in Lottery',
  skipped: 'Skipped',
  unknown: 'Unknown HC2 status',
};

export const LOAD_OUTCOME_LABELS: Record<string, string> = {
  creating: 'Creating…',
  certification_created: 'Certification created',
  invite_sent: 'Invite sent',
  appended_to_existing: 'Appended to existing cert',
  skipped_cert_exists: 'Skipped — cert exists',
  failed: 'Failed',
  not_loaded: 'Not loaded',
};

export const DUPLICATE_RESOLUTION_LABELS: Record<string, string> = {
  true_duplicate: 'True Duplicate',
  false_positive: 'False Positive',
  multiple_applications: 'Multiple Applications',
};

export const BATCH_STATE_GROUP_LABELS: Record<LotteryBatchStateGroup, string> =
  {
    in_progress: 'In progress',
    completed: 'Completed',
    partially_completed: 'Partially completed',
    failed: 'Failed',
  };

export const BATCH_STATUS_STARTING = 'Starting';
export const BATCH_STATUS_RUNNING = 'Running';
export const BATCH_STATUS_DEGRADED = 'External Services Degraded';
export const NEEDS_REVIEW_LABEL = 'Needs review';

export const BATCH_STEP_STATUS_LABELS: Record<LotteryBatchStepStatus, string> =
  {
    PENDING: 'Pending',
    RUNNING: 'Running',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    SKIPPED: 'Skipped',
  };

export function humanizeEnumValue(value: string): string {
  if (!value) {
    return '';
  }
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const NOT_LOADED_LOAD_OUTCOME = 'not_loaded';

/**
 * The label a load-outcome chip must show, guaranteed non-empty.
 *
 * Thor serialises an unset outcome as `''` (the model field defaults blank), so the
 * backend's `*_display` string is never `null`. A plain `label ?? map[value]` therefore
 * short-circuits on that empty string and renders a label-less pill. Blank is not missing
 * data here: a completed batch's never-loaded rows are exactly the "Not loaded" state the
 * status reference lists for Rejected/Ineligible/Set as Pending/In Batch. Anything else
 * unmapped falls through to its humanised enum value.
 */
export function resolveLoadOutcomeLabel(
  value: string | null | undefined,
  display?: string | null
): string {
  const shown = display?.trim();
  if (shown) {
    return shown;
  }
  const key = value?.trim() || NOT_LOADED_LOAD_OUTCOME;
  return LOAD_OUTCOME_LABELS[key] ?? humanizeEnumValue(key);
}

/**
 * The label a disposition chip must show, or `''` when there is no status at all.
 *
 * Same empty-string defect as {@link resolveLoadOutcomeLabel}: a blank `*_display` must
 * fall back to the enum map rather than short-circuit the chain. A genuinely blank
 * disposition yields `''`, which the cell renders as the table's muted dash — never a
 * label-less pill.
 */
export function resolveDispositionLabel(
  value: string | null | undefined,
  display?: string | null
): string {
  const shown = display?.trim();
  if (shown) {
    return shown;
  }
  const key = value?.trim();
  if (!key) {
    return '';
  }
  return DISPOSITION_LABELS[key] ?? humanizeEnumValue(key);
}
